import { createAsyncThunk } from '@reduxjs/toolkit';
import { GET, POST, PUT } from '../service/base-api-service';

const BASE_URL = String(process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');

const ASR_SESSIONS_PATH = 'v1/admin/site-asr/sessions';
const ASR_SESSIONS_FALLBACK_PATH = 'v1/site-asr/sessions';

const getDeleteUrl = (path) =>
  process.env.NODE_ENV === 'development'
    ? `/${path}`
    : `${BASE_URL}/${path}`;

const normalizeSessions = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

export const fetchAsrSessions = createAsyncThunk(
  'asrSessions/fetch',
  async ({ tenantId, siteId }, { rejectWithValue }) => {
    try {
      const params = { tenantId, siteId };

      try {
        const payload = await GET(ASR_SESSIONS_PATH, { params });
        return normalizeSessions(payload);
      } catch (adminError) {
        const payload = await GET(ASR_SESSIONS_FALLBACK_PATH, { params });
        return normalizeSessions(payload);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const createAsrSession = createAsyncThunk(
  'asrSessions/create',
  async (session, { rejectWithValue }) => {
    try {
      return await POST(ASR_SESSIONS_PATH, session);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateAsrSession = createAsyncThunk(
  'asrSessions/update',
  async ({ id, patch }, { rejectWithValue }) => {
    try {
      return await PUT(`${ASR_SESSIONS_PATH}/${encodeURIComponent(id)}`, patch);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const deleteAsrSession = createAsyncThunk(
  'asrSessions/delete',
  async (id, { rejectWithValue }) => {
    const token = localStorage.getItem('accessToken');
    const deleteRequest = (path) =>
      fetch(getDeleteUrl(`${path}/${encodeURIComponent(id)}`), {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          Authorization: `Bearer ${token || ''}`,
          'Content-Type': 'application/json',
        },
      });

    const getError = async (response) => {
      let message = `Unable to delete ASR session (HTTP ${response.status}).`;
      try {
        const data = await response.json();
        message = data?.message || data?.error || message;
      } catch (parseError) {
        // Delete error responses are not required to contain JSON.
      }
      return { status: response.status, message };
    };

    try {
      const deletePaths = [
        ASR_SESSIONS_PATH,
        'v1/admin/site-asr',
        ASR_SESSIONS_FALLBACK_PATH,
        'v1/site-asr',
      ];
      let response = null;

      for (const path of deletePaths) {
        response = await deleteRequest(path);
        if (response.status !== 404) break;
      }

      if (response.status === 204 || response.ok) {
        return id;
      }

      return rejectWithValue(await getError(response));
    } catch (err) {
      return rejectWithValue({
        status: err?.status,
        message: err?.message || 'Unable to delete ASR session.',
      });
    }
  },
);

export const updateAsrSessionChamber = createAsyncThunk(
  'asrSessions/updateChamber',
  async ({ sessionId, chamberId, patch }, { rejectWithValue }) => {
    try {
      return await PUT(
        `${ASR_SESSIONS_PATH}/${encodeURIComponent(sessionId)}/chambers/${encodeURIComponent(chamberId)}`,
        patch,
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
